// src/components/admin/compensation/CompensationPlanWizard.jsx
import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2, FiInfo, FiHelpCircle } from 'react-icons/fi';
import { tenantDataService } from '../../../services/tenantDataService';
import { createCompensationPlan, updateCompensationPlan } from '../../../services/compPlanService';
import toast from 'react-hot-toast';

// Tooltip component for help text
const Tooltip = ({ children, text, example }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="relative inline-block">
      <div 
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {showTooltip && (
        <div className="absolute z-50 w-80 p-4 bg-gray-900 text-white text-sm rounded-lg shadow-lg -top-2 left-8">
          <div className="mb-2">{text}</div>
          {example && (
            <div className="text-blue-200 bg-blue-900/30 p-2 rounded text-xs">
              <strong>Example:</strong> {example}
            </div>
          )}
          <div className="absolute top-3 -left-2 w-0 h-0 border-t-4 border-b-4 border-r-8 border-transparent border-r-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function CompensationPlanWizard({ initialPlan, onClose }) {
  const [step, setStep] = useState(1);
  const [plan, setPlan] = useState({
    name: '',
    description: '',
    scope: 'all', // 'all', 'specific'
    commissionBasis: 'billed', // 'billed', 'collected'
    threshold: {
      type: 'none', // 'none', 'draw', 'minimum'
      amount: 0
    },
    tiers: [
      { from: 0, to: null, rate: 0.5 }
    ],
    deductions: [],
    serviceOverrides: [],
    specials: {
      noShowCommission: 0,
      lateCancelCommission: 0,
      discountedServiceRate: 1.0,
      packageServiceRate: 1.0
    },
    active: true
  });

  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTreatments();
    if (initialPlan) {
      setPlan({
        ...initialPlan,
        threshold: {
          type: 'none',
          amount: 0,
          ...initialPlan.threshold
        },
        tiers: initialPlan.tiers || [{ from: 0, to: null, rate: 0.5 }],
        deductions: initialPlan.deductions || [],
        serviceOverrides: initialPlan.serviceOverrides || [],
        specials: {
          noShowCommission: 0,
          lateCancelCommission: 0,
          discountedServiceRate: 1.0,
          packageServiceRate: 1.0,
          ...initialPlan.specials
        }
      });
    }
  }, [initialPlan]);

  const loadTreatments = async () => {
    try {
      const settings = await tenantDataService.getById('settings', 'clinic');
      setTreatments(settings?.treatments || []);
    } catch (error) {
      console.error('Error loading treatments:', error);
    }
  };

  const updatePlan = (field, value) => {
    setPlan(prev => ({ ...prev, [field]: value }));
  };

  const updateNested = (field, subfield, value) => {
    setPlan(prev => ({
      ...prev,
      [field]: { ...prev[field], [subfield]: value }
    }));
  };

  const addTier = () => {
    const lastTier = plan.tiers?.[plan.tiers.length - 1];
    const newFrom = lastTier?.to || 0;
    
    setPlan(prev => ({
      ...prev,
      tiers: [
        ...prev.tiers.slice(0, -1),
        { ...lastTier, to: newFrom },
        { from: newFrom, to: null, rate: 0.5 }
      ]
    }));
  };

  const updateTier = (index, field, value) => {
    setPlan(prev => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => 
        i === index ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const removeTier = (index) => {
    if (plan.tiers.length <= 1) return;
    
    setPlan(prev => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index)
    }));
  };

  const addDeduction = () => {
    setPlan(prev => ({
      ...prev,
      deductions: [
        ...prev.deductions,
        { name: '', amount: 0, type: 'fixed' }
      ]
    }));
  };

  const updateDeduction = (index, field, value) => {
    setPlan(prev => ({
      ...prev,
      deductions: prev.deductions.map((deduction, i) => 
        i === index ? { ...deduction, [field]: value } : deduction
      )
    }));
  };

  const removeDeduction = (index) => {
    setPlan(prev => ({
      ...prev,
      deductions: prev.deductions.filter((_, i) => i !== index)
    }));
  };

  const addServiceOverride = () => {
    setPlan(prev => ({
      ...prev,
      serviceOverrides: [
        ...prev.serviceOverrides,
        { treatmentType: '', rate: 0.5 }
      ]
    }));
  };

  const updateServiceOverride = (index, field, value) => {
    setPlan(prev => ({
      ...prev,
      serviceOverrides: prev.serviceOverrides.map((override, i) => 
        i === index ? { ...override, [field]: value } : override
      )
    }));
  };

  const removeServiceOverride = (index) => {
    setPlan(prev => ({
      ...prev,
      serviceOverrides: prev.serviceOverrides.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validation
      if (!plan.name.trim()) {
        toast.error('Plan name is required');
        return;
      }
      
      if (plan.tiers.some(tier => tier.rate < 0 || tier.rate > 1)) {
        toast.error('Commission rates must be between 0% and 100%');
        return;
      }

      const planData = {
        ...plan,
        name: plan.name.trim(),
        description: plan.description.trim(),
        updatedAt: new Date()
      };

      if (initialPlan) {
        await updateCompensationPlan(initialPlan.id, planData);
        toast.success('Compensation plan updated successfully');
      } else {
        await createCompensationPlan(planData);
        toast.success('Compensation plan created successfully');
      }
      
      onClose(true);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save compensation plan');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Plan Name *
          </label>
          <Tooltip 
            text="Give this plan a clear, descriptive name that staff will recognize."
            example="'Senior Physiotherapist', 'Junior Clinician', 'Admin Default'"
          >
            <FiHelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <input
          type="text"
          value={plan.name}
          onChange={(e) => updatePlan('name', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., Senior Clinician Plan"
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <Tooltip 
            text="Optional: Add details about when this plan applies and any special conditions."
            example="'For clinicians with 2+ years experience' or 'Standard rate for all new hires'"
          >
            <FiHelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <textarea
          value={plan.description}
          onChange={(e) => updatePlan('description', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Optional description of this compensation plan"
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Commission Basis
          </label>
          <Tooltip 
            text="Choose when staff earn commission - when you send the invoice, or when the client actually pays."
            example="Most clinics use 'Billed' so staff get paid immediately when treatment is invoiced."
          >
            <FiHelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center p-4 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="commissionBasis"
              value="billed"
              checked={plan.commissionBasis === 'billed'}
              onChange={(e) => updatePlan('commissionBasis', e.target.value)}
              className="mr-3"
            />
            <div>
              <div className="font-medium">Billed Amount</div>
              <div className="text-sm text-gray-500">Staff earn commission when invoice is sent</div>
              <div className="text-xs text-green-600 mt-1">✓ Recommended for most clinics</div>
            </div>
          </label>
          <label className="flex items-center p-4 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="commissionBasis"
              value="collected"
              checked={plan.commissionBasis === 'collected'}
              onChange={(e) => updatePlan('commissionBasis', e.target.value)}
              className="mr-3"
            />
            <div>
              <div className="font-medium">Collected Amount</div>
              <div className="text-sm text-gray-500">Staff earn commission when client pays</div>
              <div className="text-xs text-orange-600 mt-1">⚠ Only if you have payment delays</div>
            </div>
          </label>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Minimum Earnings (Optional)
          </label>
          <Tooltip 
            text="Set a minimum monthly earning guarantee for staff. Most clinics leave this as 'No Minimum' to keep it simple."
            example="If set to £2000, staff will earn at least £2000 even if their commission is lower."
          >
            <FiHelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <select
          value={plan.threshold.type}
          onChange={(e) => updateNested('threshold', 'type', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="none">No Minimum (Simple commission only)</option>
          <option value="minimum">Guaranteed Minimum Earnings</option>
          <option value="draw">Monthly Draw (Advanced)</option>
        </select>
        
        {plan.threshold.type !== 'none' && (
          <div className="mt-3">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-3">
              <div className="text-sm text-blue-800">
                {plan.threshold.type === 'minimum' && (
                  <div>
                    <strong>Guaranteed Minimum:</strong> Staff will earn at least this amount each month, even if commission is lower.
                  </div>
                )}
                {plan.threshold.type === 'draw' && (
                  <div>
                    <strong>Monthly Draw:</strong> Staff receive this amount monthly, then commission is calculated against it. Advanced feature - contact support if unsure.
                  </div>
                )}
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (£)
            </label>
            <input
              type="number"
              min="0"
              step="100"
              value={plan.threshold.amount || ''}
              onChange={(e) => updateNested('threshold', 'amount', parseFloat(e.target.value) || 0)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., 2000"
            />
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Commission Basis
          </label>
          <Tooltip 
            text="Choose when staff earn commission - when you send the invoice, or when the client actually pays."
            example="Most clinics use 'Billed' so staff get paid immediately when treatment is invoiced."
          >
            <FiHelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center p-4 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="commissionBasis"
              value="billed"
              checked={plan.commissionBasis === 'billed'}
              onChange={(e) => updatePlan('commissionBasis', e.target.value)}
              className="mr-3"
            />
            <div>
              <div className="font-medium">Billed Amount</div>
              <div className="text-sm text-gray-500">Staff earn commission when invoice is sent</div>
              <div className="text-xs text-green-600 mt-1">✓ Recommended for most clinics</div>
            </div>
          </label>
          <label className="flex items-center p-4 border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="commissionBasis"
              value="collected"
              checked={plan.commissionBasis === 'collected'}
              onChange={(e) => updatePlan('commissionBasis', e.target.value)}
              className="mr-3"
            />
            <div>
              <div className="font-medium">Collected Amount</div>
              <div className="text-sm text-gray-500">Staff earn commission when client pays</div>
              <div className="text-xs text-orange-600 mt-1">⚠ Only if you have payment delays</div>
            </div>
          </label>
        </div>
      </div>

    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">Commission Tiers</h3>
            <Tooltip 
              text="Set different commission rates based on monthly earnings. Most clinics start with one simple tier."
              example="£0-£5000 = 50%, £5000+ = 60%. Staff earn higher rates as they bring in more revenue."
            >
              <FiHelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </div>
          <button
            onClick={addTier}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiPlus size={14} />
            Add Tier
          </button>
        </div>
        
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl mb-4">
          <div className="text-sm text-green-800">
            <strong>Simple Setup:</strong> Most clinics use just one tier (0 to unlimited) with a single commission rate like 50%.
            <br />
            <strong>Advanced Setup:</strong> Add multiple tiers to reward high performers (e.g., 50% up to £5000, then 60% above).
          </div>
        </div>
        
        <div className="space-y-3">
          {plan.tiers.map((tier, index) => (
            <div key={index} className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl">
              <div className="flex-1 grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From (£)</label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={tier.from}
                    onChange={(e) => updateTier(index, 'from', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To (£)</label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={tier.to || ''}
                    onChange={(e) => updateTier(index, 'to', e.target.value ? parseFloat(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="No limit (recommended)"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Commission Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={(tier.rate * 100).toFixed(1)}
                    onChange={(e) => updateTier(index, 'rate', (parseFloat(e.target.value) || 0) / 100)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              {plan.tiers.length > 1 && (
                <button
                  onClick={() => removeTier(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Deductions</h3>
          <button
            onClick={addDeduction}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiPlus size={14} />
            Add Deduction
          </button>
        </div>
        
        {plan.deductions.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            No deductions configured
          </div>
        ) : (
          <div className="space-y-3">
            {plan.deductions.map((deduction, index) => (
              <div key={index} className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl">
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                      type="text"
                      value={deduction.name}
                      onChange={(e) => updateDeduction(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="e.g., Admin Fee"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select
                      value={deduction.type}
                      onChange={(e) => updateDeduction(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="fixed">Fixed Amount</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Amount {deduction.type === 'percentage' ? '(%)' : '(£)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={deduction.amount}
                      onChange={(e) => updateDeduction(index, 'amount', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeDeduction(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">Service Overrides</h3>
            <Tooltip 
              text="Set different commission rates for specific treatments. Most clinics can skip this section."
              example="Massage = 40%, Physiotherapy = 60%. Use this if some treatments should have different rates."
            >
              <FiHelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </div>
          <button
            onClick={addServiceOverride}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiPlus size={14} />
            Add Override
          </button>
        </div>
        
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <div className="text-sm text-blue-800">
            <strong>Optional:</strong> Most clinics use the same commission rate for all services. Only add overrides if you want different rates for specific treatments (e.g., lower rate for basic consultations, higher for specialized procedures).
          </div>
        </div>
        
        {plan.serviceOverrides.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
            <div className="mb-2">No service overrides configured</div>
            <div className="text-xs text-gray-400">All services will use the standard commission tiers above</div>
          </div>
        ) : (
          <div className="space-y-3">
            {plan.serviceOverrides.map((override, index) => (
              <div key={index} className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Treatment Type</label>
                    <select
                      value={override.treatmentType}
                      onChange={(e) => updateServiceOverride(index, 'treatmentType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select treatment</option>
                      {treatments.map(treatment => (
                        <option key={treatment.name} value={treatment.name}>
                          {treatment.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={(override.rate * 100).toFixed(1)}
                      onChange={(e) => updateServiceOverride(index, 'rate', (parseFloat(e.target.value) || 0) / 100)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeServiceOverride(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-medium text-gray-900">Special Situations</h3>
          <Tooltip 
            text="Set commission rates for special situations like no-shows or discounted services. Most clinics can use the default values."
            example="No-show = 0% (no commission for missed appointments), Discounted = 100% (full commission even on discounted services)"
          >
            <FiHelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl mb-4">
          <div className="text-sm text-yellow-800">
            <strong>Default Settings Work for Most Clinics:</strong> These handle edge cases like missed appointments and discounted services. The default values are recommended unless you have specific requirements.
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                No-Show Commission (%)
              </label>
              <Tooltip 
                text="Commission rate when clients don't show up for appointments."
                example="Usually 0% - staff don't earn commission for missed appointments."
              >
                <FiHelpCircle className="w-3 h-3 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={((plan.specials?.noShowCommission || 0) * 100).toFixed(1)}
              onChange={(e) => updateNested('specials', 'noShowCommission', (parseFloat(e.target.value) || 0) / 100)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Late Cancel Commission (%)
              </label>
              <Tooltip 
                text="Commission rate for last-minute cancellations (usually within 24 hours)."
                example="Usually 0% - staff don't earn commission for late cancellations."
              >
                <FiHelpCircle className="w-3 h-3 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={((plan.specials?.lateCancelCommission || 0) * 100).toFixed(1)}
              onChange={(e) => updateNested('specials', 'lateCancelCommission', (parseFloat(e.target.value) || 0) / 100)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Discounted Service Rate (multiplier)
              </label>
              <Tooltip 
                text="How much commission staff earn on discounted services. 1.0 = full commission, 0.5 = half commission."
                example="1.0 = Staff get full commission even on discounted services (recommended)."
              >
                <FiHelpCircle className="w-3 h-3 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={plan.specials?.discountedServiceRate || 1.0}
              onChange={(e) => updateNested('specials', 'discountedServiceRate', parseFloat(e.target.value) || 1.0)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Package Service Rate (multiplier)
              </label>
              <Tooltip 
                text="Commission rate for services sold as part of packages or bundles."
                example="1.0 = Full commission on package services (recommended)."
              >
                <FiHelpCircle className="w-3 h-3 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={plan.specials?.packageServiceRate || 1.0}
              onChange={(e) => updateNested('specials', 'packageServiceRate', parseFloat(e.target.value) || 1.0)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const steps = [
    { number: 1, title: 'Basic Settings', component: renderStep1 },
    { number: 2, title: 'Tiers & Deductions', component: renderStep2 },
    { number: 3, title: 'Overrides & Specials', component: renderStep3 }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {initialPlan ? 'Edit Compensation Plan' : 'Create Compensation Plan'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Step {step} of {steps.length}: {steps[step - 1].title}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between">
            {steps.map((s, index) => (
              <div key={s.number} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s.number 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {s.number}
                </div>
                <div className="ml-3 text-sm font-medium text-gray-900">
                  {s.title}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    step > s.number ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {steps[step - 1].component()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={() => onClose(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {step < steps.length ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (initialPlan ? 'Update Plan' : 'Create Plan')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
